/** Talking to the backend.
 *
 * The backend has its own address in production (meetings-be.<domain>), so
 * every call is cross-origin: `credentials: "include"` to send the session
 * cookie, and the backend allows exactly this origin. In development
 * VITE_API_BASE is empty and Vite proxies /api to localhost:8000, which keeps
 * it same-origin and needs no CORS at all.
 */

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

function url(path: string): string {
  return API_BASE + path;
}

export class NotSignedIn extends Error {
  constructor() {
    super("Not signed in");
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

/** FastAPI's error text. `detail` is a sentence for the errors we raise, but a
 * list of field problems when it rejects the request itself — which as a plain
 * string reads "[object Object]". */
function readDetail(body: unknown): string | null {
  if (typeof body !== "object" || body === null || !("detail" in body)) return null;
  const detail = (body as { detail: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const problems = detail
      .map((item) => (typeof item === "object" && item !== null && "msg" in item ? String((item as { msg: unknown }).msg) : null))
      .filter(Boolean);
    if (problems.length > 0) return problems.join("; ");
  }
  return JSON.stringify(detail);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url(path), {
    // The cookie lives on the backend's domain, so "same-origin" would drop it.
    credentials: "include",
    ...init,
    // After ...init, or init's own headers would replace these instead of
    // joining them — and a POST would go out with no Content-Type.
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });

  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, readDetail(body) ?? `HTTP ${response.status}`);
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown, csrfToken?: string) =>
    request<T>(path, {
      method: "POST",
      headers: csrfToken ? { "X-CSRF-Token": csrfToken } : undefined,
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T,>(path: string, body: unknown, csrfToken: string) =>
    request<T>(path, {
      method: "PUT",
      headers: { "X-CSRF-Token": csrfToken },
      body: JSON.stringify(body),
    }),
  delete: <T = void,>(path: string, csrfToken: string) =>
    request<T>(path, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken } }),
};

/** Google sign-in happens on the backend; it sends the browser back here. */
export function signIn(): void {
  window.location.href = url("/auth/google");
}

/** The backend's own sign-in page with its admin password form open: for
 * admins without a Polaris Google account (ADMIN_EMAILS), or when Google
 * sign-in is down. It sends the browser back here afterwards. */
/** "Connect with Teams": Microsoft's sign-in and consent, then back here. */
export function connectTeams(): void {
  window.location.href = url("/auth/microsoft");
}

export function adminSignIn(): void {
  window.location.href = url("/login?admin=1");
}

/** Ends the session, then reloads so nothing from it stays in memory and the
 * sign-in screen shows. Reloads even if the backend call fails: an expired
 * session is signed out already. */
export async function signOut(csrfToken: string): Promise<void> {
  try {
    await api.post("/api/logout", undefined, csrfToken);
  } finally {
    window.location.assign("/");
  }
}

// --- what the backend returns (src/api/routes_api.py) ----------------------

export interface Me {
  email: string;
  name: string | null;
  is_admin: boolean;
  csrf_token: string;
  /** "Connect with Teams": offered on this server, and whether you've connected (and which account). */
  teams?: { available: boolean; connected: boolean; email: string | null; error: string | null };
  /** Minutes emailed after each meeting you were invited to (the profile menu's switch). */
  email_minutes?: boolean;
  /** The name the bot joins under (BOT_NAME on the server). */
  bot_name?: string;
  /** The lists show everyone's meetings and projects: an admin with the top
   * bar's switch on Everyone. */
  sees_all?: boolean;
}

export interface Meeting {
  session_id: string | null;
  meet_url: string | null;
  owner_email: string | null;
  requested_at: string | null;
  title: string | null;
  starts_at: string | null;
  /** The calendar's end time; null for a pasted link. */
  ends_at: string | null;
  state: string;
  label: string;
  active: boolean;
  can_stop: boolean;
  has_video: boolean;
  has_audio: boolean;
  transcript: "ready" | "pending" | "none";
  duration_seconds: number | null;
  /** Recorded on the Record page: no link, no video, just the room's audio. */
  in_person?: boolean;
  /** Set when a colleague shared this meeting with you: who, and when. */
  shared_by?: string | null;
  shared_at?: string | null;
  /** The projects it's in that you can see. */
  projects?: ProjectRef[] | null;
  /** meet, teams, zoom, in_person, or other: an upload whose app couldn't be told. */
  platform?: MeetingPlatform | null;
  /** From the Upload page; upload_note says what happened to the file. */
  uploaded?: boolean;
  /** Recorded, and yours to rename (whose bot it was, the organizer, or an admin). */
  can_rename?: boolean;
  upload_note?: string | null;
}

export type MeetingPlatform = "meet" | "teams" | "zoom" | "in_person" | "other";

/** An upload, started: pieces of `piece_bytes` go up in order from `next`. */
export interface UploadStarted {
  session_id: string;
  title: string;
  piece_bytes: number;
  next: number;
}

/** One of your uploads that was started and never finished: picking the same
 * file again carries on from piece `next`. */
export interface UnfinishedUpload {
  session_id: string;
  title: string;
  created_at: string;
  file_name: string | null;
  size: number;
  bytes: number;
  next: number;
  piece_bytes: number;
}

export interface ProjectRef {
  id: number;
  name: string;
}

export interface ProjectMember {
  id: number;
  email: string;
  name: string | null;
  /** Made the project. */
  creator: boolean;
  you: boolean;
}

/** A project as the Projects page lists it. */
export interface Project extends ProjectRef {
  description: string | null;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
  meeting_count: number;
  last_meeting_at: string | null;
  members: ProjectMember[];
  /** Made it, or an admin: may rename, delete, and add or remove people. */
  can_manage: boolean;
}

/** Where a point in a project's Overview or Timeline comes from: a meeting,
 * and the moment in it (null when no moment fits). */
export interface ProjectCite {
  session_id: string;
  title: string;
  date: string | null;
  at: string | null;
  seconds: number | null;
}

export interface ProjectPoint {
  text: string;
  cites: ProjectCite[];
}

/** Written by AI from every ready meeting of the project (src/minutes/project.py). */
export interface ProjectOverview {
  about: ProjectPoint;
  status: ProjectPoint;
  decisions: ProjectPoint[];
  action_items: { task: string; owner: string | null; due: string | null; done: boolean; cites: ProjectCite[] }[];
  open_questions: ProjectPoint[];
  people: { name: string; role: string }[];
}

export interface ProjectThread {
  title: string;
  state: "settled" | "open";
  steps: (ProjectCite & { text: string })[];
}

export interface ProjectInsights {
  /** null: never written yet. */
  status: "queued" | "running" | "done" | "failed" | null;
  error: string | null;
  written_at: string | null;
  overview: ProjectOverview | null;
  threads: ProjectThread[] | null;
  meetings_used: { session_id: string; title: string; date: string | null; as: "transcript" | "minutes" }[];
  meetings: number;
  /** Meetings still being processed: included once they're ready. */
  pending: number;
  /** Newest first: each ready meeting's 2-4 outcomes. */
  by_meeting: {
    session_id: string;
    title: string;
    date: string | null;
    points: { text: string; at: string | null; seconds: number | null }[];
    has_minutes: boolean;
    /** False while its entry is being written for its latest minutes. */
    current: boolean;
  }[];
}

interface SearchMeeting {
  session_id: string;
  title: string;
  date: string | null;
}

/** A project searched for some words (GET /api/projects/<id>/search). */
export interface ProjectSearchResult {
  query: string;
  counts: { overview: number; timeline: number; minutes: number; transcripts: number };
  overview: { section: string; text: string; owner?: string | null; due?: string | null; done?: boolean;
              cites: SearchMeeting[] }[];
  timeline: {
    topics: { title: string; state: "settled" | "open"; title_match: boolean;
              steps: (SearchMeeting & { text: string; seconds: number | null })[] }[];
    meetings: (SearchMeeting & { points: { text: string; seconds: number | null }[] })[];
  };
  minutes: (SearchMeeting & {
    items: { kind: "topic" | "discussion" | "action" | "agenda"; topic?: string | null; text: string | null;
             owner?: string | null; due?: string | null; seconds: number | null }[];
  })[];
  transcripts: (SearchMeeting & { lines: { seconds: number; speaker: string; text: string }[]; more: number })[];
}

/** A project's own page: its numbers and its meetings. */
export interface ProjectDetail extends Project {
  metrics: { meetings: number; seconds: number; action_items: number };
  meetings: (Meeting & { added_by: string | null; can_remove: boolean })[];
}

/** One turn in a transcript: who spoke, when, and what they said. Built by
 * src/transcription/transcript.py, whichever service did the work. */
export interface Utterance {
  speaker: string;
  start: number;
  end: number;
  text: string;
}

export interface TranscriptData {
  session_id: string;
  language_code: string | null;
  language_probability: number | null;
  duration_seconds: number | null;
  provider: string;
  model: string;
  speakers: string[];
  utterances: Utterance[];
}

/** One meeting with its transcript: what /api/recordings/<session-id> returns.
 *
 * `video_url` and `audio_url` are signed links straight to S3, so a recording
 * plays from there instead of through the app. They expire, and they're null
 * when the file is on the app's own disk instead — then `fileUrl` below is the
 * way to reach it. */
/** The minutes of meeting (src/minutes/schema.py MinutesOfMeeting). The facts
 * come from the meeting itself; agenda, discussion and action items from the
 * LLM, which wrote them from the transcript. */
export interface MinutesDocument {
  heading: string;
  title: string;
  info: {
    starts_at: string | null;
    duration_minutes: number | null;
    platform: string;
    link: string | null;
  };
  /** The calendar invite first, then anyone else heard named in the call. */
  attendees: string[];
  agenda: string[];
  discussion: { topic: string; summary: string }[];
  /** owner: who's doing it, by name; missing from minutes written before owners. */
  /** said_at: hh:mm:ss in the recording where it was agreed; minutes before mom-10 have none. */
  action_items: { task: string; owner?: string | null; due: string | null; said_at?: string | null }[];
}

/** Where the minutes are. A regenerate keeps the last document here, with the
 * status saying new ones are on the way. */
export interface MinutesState {
  status: "queued" | "running" | "done" | "failed" | "skipped";
  document: MinutesDocument | null;
  error: string | null;
  model: string | null;
  updated_at: string;
}

/** One message in your chat about a meeting. */
export interface ChatMessage {
  role: "user" | "assistant";
  /** Markdown, for the model's replies. */
  content: string;
  created_at?: string;
}

/** Your own standing instruction for Personalized Details. */
export interface Personalization {
  text: string;
  updated_at: string | null;
}

/** Your Personalized Details for one meeting — only ever yours. */
export interface PersonalState {
  status: "queued" | "running" | "done" | "failed" | null;
  /** Markdown. */
  content: string | null;
  error: string | null;
  updated_at: string | null;
  /** Whether you've set an instruction at all. */
  has_instruction: boolean;
  /** You've edited your instruction since this was written. */
  instruction_changed: boolean;
  /** A speaker's name was worked out or corrected since these were written. */
  names_changed?: boolean;
}

/** The transcript's lines in Roman Urdu, when it had any in Devanagari or
 * Urdu script. Once written, they're already in transcript_data. */
export interface RomanState {
  status: "queued" | "running" | "done" | "failed" | "skipped";
  error: string | null;
  /** transcript_data holds the Roman Urdu lines. */
  shown: boolean;
}

/** Who one voice in the transcript is. */
export interface SpeakerName {
  /** Null: someone said "not sure", so the label shows. */
  name: string | null;
  /** "ai": worked out from the conversation; "person": set on this page. */
  by: "ai" | "person";
  /** The AI's quote that shows it. */
  evidence?: string;
  how?: string;
  /** Who set it, for "person". */
  by_user?: string;
}

/** The transcript's speakers: label ("Speaker 2") → who it is. */
export interface SpeakersState {
  status: "queued" | "running" | "done" | "failed" | "skipped" | null;
  names: Record<string, SpeakerName>;
  updated_at: string | null;
  /** The invitees, offered when correcting a name. */
  candidates: string[];
}

export interface MeetingDetail extends Meeting {
  /** Null until there's a finished transcript. */
  speakers: SpeakersState | null;
  transcript_data: TranscriptData | null;
  /** Null for a transcript that never needed Roman Urdu. */
  transcript_roman: RomanState | null;
  minutes: MinutesState | null;
  personal: PersonalState;
  /** How many invitees the minutes were emailed to. */
  emailed?: number;
  video_url: string | null;
  audio_url: string | null;
}

/** What POST /api/meetings answers with: one entry per link it took. */
export interface QueuedMeeting {
  session_id: string;
  meet_url: string;
  start_in_minutes: number | null;
}

/** One person who can see a meeting, and why (the Share dialog). */
export interface AccessPerson {
  email: string;
  /** Null until they've signed in. */
  name: string | null;
  access: "organizer" | "bot" | "invited" | "shared" | "project";
  you: boolean;
  shared_by: string | null;
  /** For access "project": which project. */
  project?: string;
  can_remove: boolean;
}

export interface Access {
  people: AccessPerson[];
  /** Only accounts on this domain can be added. */
  domain: string;
}

export interface Colleague {
  email: string;
  name: string | null;
}

/** An in-person recording, from the Record page: started, then sent in pieces. */
export interface RoomRecording {
  session_id: string;
  title: string;
  /** The longest it may run; the page stops and saves at this. */
  max_seconds: number;
}

/** One of your own recordings that was started but never finished (the page
 * closed, the phone died): what has arrived can still be saved. */
export interface UnfinishedRecording {
  session_id: string;
  title: string;
  started_at: string;
  /** How much audio has arrived, roughly. */
  seconds: number;
}

export interface Status {
  workers_responding: boolean;
  meetings_recording: number;
  free_workers: number;
  transcriptions_running: number;
  transcriber_running: boolean;
}

export const endpoints = {
  me: () => api.get<Me>("/api/me"),
  meetings: () => api.get<Meeting[]>("/api/meetings"),
  recordings: () => api.get<{ past: Meeting[]; upcoming: Meeting[] }>("/api/recordings"),
  recording: (sessionId: string) => api.get<MeetingDetail>(`/api/recordings/${encodeURIComponent(sessionId)}`),
  status: () => api.get<Status>("/api/status"),
  startMeetings: (urls: string[], csrfToken: string) =>
    api.post<QueuedMeeting[]>("/api/meetings", { urls }, csrfToken),
  personalization: () => api.get<Personalization>("/api/me/personalization"),
  savePersonalization: (text: string, csrfToken: string) =>
    api.put<Personalization>("/api/me/personalization", { text }, csrfToken),
  generatePersonal: (sessionId: string, csrfToken: string) =>
    api.post<{ status: string }>(`/api/recordings/${encodeURIComponent(sessionId)}/personal`, undefined, csrfToken),
  chatHistory: (sessionId: string) =>
    api.get<{ messages: ChatMessage[] }>(`/api/recordings/${encodeURIComponent(sessionId)}/chat`),
  clearChat: (sessionId: string, csrfToken: string) =>
    api.delete(`/api/recordings/${encodeURIComponent(sessionId)}/chat`, csrfToken),
  regenerateMinutes: (sessionId: string, csrfToken: string) =>
    api.post<{ status: string }>(`/api/recordings/${encodeURIComponent(sessionId)}/minutes`, undefined, csrfToken),
  stopMeeting: (sessionId: string, csrfToken: string) =>
    api.post<{ message: string }>(`/api/meetings/${sessionId}/stop`, undefined, csrfToken),
  access: (sessionId: string) => api.get<Access>(`/api/recordings/${encodeURIComponent(sessionId)}/access`),
  share: (sessionId: string, emails: string[], csrfToken: string) =>
    api.post<Access>(`/api/recordings/${encodeURIComponent(sessionId)}/share`, { emails }, csrfToken),
  unshare: (sessionId: string, email: string, csrfToken: string) =>
    api.delete<Access>(
      `/api/recordings/${encodeURIComponent(sessionId)}/share/${encodeURIComponent(email)}`,
      csrfToken,
    ),
  colleagues: () => api.get<Colleague[]>("/api/colleagues"),
  setEmailMinutes: (enabled: boolean, csrfToken: string) =>
    api.put<{ email_minutes: boolean }>("/api/me/email-minutes", { enabled }, csrfToken),
  setAdminView: (everyone: boolean, csrfToken: string) =>
    api.put<{ sees_all: boolean }>("/api/me/admin-view", { everyone }, csrfToken),
  projects: () => api.get<Project[]>("/api/projects"),
  project: (id: number) => api.get<ProjectDetail>(`/api/projects/${id}`),
  createProject: (body: { name: string; description: string | null; emails: string[] }, csrfToken: string) =>
    api.post<ProjectDetail>("/api/projects", body, csrfToken),
  updateProject: (id: number, body: { name: string; description: string | null }, csrfToken: string) =>
    api.put<ProjectDetail>(`/api/projects/${id}`, body, csrfToken),
  deleteProject: (id: number, csrfToken: string) => api.delete(`/api/projects/${id}`, csrfToken),
  addProjectMembers: (id: number, emails: string[], csrfToken: string) =>
    api.post<ProjectDetail>(`/api/projects/${id}/members`, { emails }, csrfToken),
  /** Answers { left: true } when you took yourself off. */
  removeProjectMember: (id: number, userId: number, csrfToken: string) =>
    api.delete<ProjectDetail | { left: true }>(`/api/projects/${id}/members/${userId}`, csrfToken),
  addProjectMeetings: (id: number, sessionIds: string[], csrfToken: string) =>
    api.post<ProjectDetail>(`/api/projects/${id}/meetings`, { session_ids: sessionIds }, csrfToken),
  projectInsights: (id: number) => api.get<ProjectInsights>(`/api/projects/${id}/insights`),
  searchProject: (id: number, query: string) =>
    api.get<ProjectSearchResult>(`/api/projects/${id}/search?q=${encodeURIComponent(query)}`),
  updateProjectInsights: (id: number, csrfToken: string) =>
    api.post<ProjectInsights>(`/api/projects/${id}/insights`, undefined, csrfToken),
  removeProjectMeeting: (id: number, sessionId: string, csrfToken: string) =>
    api.delete<ProjectDetail>(`/api/projects/${id}/meetings/${encodeURIComponent(sessionId)}`, csrfToken),
  disconnectTeams: (csrfToken: string) => api.delete("/api/me/teams", csrfToken),
  startRecording: (body: { title: string; attendees: string[]; mime: string }, csrfToken: string) =>
    api.post<RoomRecording>("/api/room-recordings", body, csrfToken),
  finishRecording: (sessionId: string, body: { duration_seconds: number; attendees: string[] }, csrfToken: string) =>
    api.post<{ session_id: string }>(`/api/room-recordings/${encodeURIComponent(sessionId)}/finish`, body, csrfToken),
  unfinishedRecordings: () => api.get<UnfinishedRecording[]>("/api/room-recordings/unfinished"),
  startUpload: (body: { file_name: string; size: number; title: string; starts_at: string | null;
                        attendees: string[]; platform: "auto" | Exclude<MeetingPlatform, "other"> }, csrfToken: string) =>
    api.post<UploadStarted>("/api/uploads", body, csrfToken),
  finishUpload: (sessionId: string, csrfToken: string) =>
    api.post<{ session_id: string }>(`/api/uploads/${encodeURIComponent(sessionId)}/finish`, undefined, csrfToken),
  unfinishedUploads: () => api.get<UnfinishedUpload[]>("/api/uploads/unfinished"),
  deleteUpload: (sessionId: string, csrfToken: string) =>
    api.delete(`/api/uploads/${encodeURIComponent(sessionId)}`, csrfToken),
  renameMeeting: (sessionId: string, title: string, csrfToken: string) =>
    api.put<{ title: string }>(`/api/recordings/${encodeURIComponent(sessionId)}/title`, { title }, csrfToken),
  setUploadPlatform: (sessionId: string, platform: MeetingPlatform, csrfToken: string) =>
    api.put<{ platform: MeetingPlatform }>(`/api/uploads/${encodeURIComponent(sessionId)}/platform`, { platform }, csrfToken),
  deleteRecording: (sessionId: string, csrfToken: string) =>
    api.delete(`/api/room-recordings/${encodeURIComponent(sessionId)}`, csrfToken),
  renameSpeaker: (sessionId: string, label: string, name: string | null, csrfToken: string) =>
    api.put<SpeakersState>(
      `/api/recordings/${encodeURIComponent(sessionId)}/speakers/${encodeURIComponent(label)}`,
      { name },
      csrfToken,
    ),
};

/** Asks a question about a meeting and hands the answer to `onText` piece by
 * piece as the model writes it. The response is one JSON object per line:
 * {type: "delta", text}, then {type: "done"} — or {type: "error", message} if
 * it fails part-way, after the response has already started. Resolves once
 * the answer is complete (and saved to your history); rejects otherwise. */
export async function streamChat(
  sessionId: string,
  message: string,
  csrfToken: string,
  onText: (text: string) => void,
): Promise<void> {
  const response = await fetch(url(`/api/recordings/${encodeURIComponent(sessionId)}/chat`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ message }),
  });
  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok || !response.body) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, readDetail(body) ?? `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, { stream: true });
    let newline: number;
    while ((newline = buffered.indexOf("\n")) >= 0) {
      const line = buffered.slice(0, newline).trim();
      buffered = buffered.slice(newline + 1);
      if (!line) continue;
      const event = JSON.parse(line) as { type: string; text?: string; message?: string };
      if (event.type === "delta" && event.text) onText(event.text);
      else if (event.type === "error") throw new ApiError(502, event.message ?? "The answer failed.");
      else if (event.type === "done") return;
    }
  }
  throw new ApiError(502, "The answer stopped before it finished.");
}

/** Where the recording's files live. These aren't fetched with `request`: the
 * browser loads them itself, as a <video> source or a download. They're on the
 * backend's origin, so a <video> has to be told to send the session cookie
 * (crossOrigin="use-credentials") or the request arrives signed out and 401s. */
/** Sends one piece of an in-person recording. Raw audio, not JSON — so not
 * through `request`. Pieces go in order; `seq` lets the server ignore a piece
 * it already has, when a retry follows a reply that got lost. */
export async function uploadRecordingPiece(sessionId: string, seq: number, piece: Blob,
                                           csrfToken: string): Promise<void> {
  const response = await fetch(url(`/api/room-recordings/${encodeURIComponent(sessionId)}/pieces?seq=${seq}`), {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken, "Content-Type": piece.type || "application/octet-stream" },
    body: piece,
  });
  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, readDetail(body) ?? `HTTP ${response.status}`);
  }
}

/** Sends one piece of an uploaded file, reporting how much of it has gone so
 * a big piece on a slow line still moves the progress bar. XMLHttpRequest,
 * not fetch: only it reports upload progress. */
export function uploadFilePiece(sessionId: string, seq: number, piece: Blob, csrfToken: string,
                                onProgress: (sent: number) => void, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url(`/api/uploads/${encodeURIComponent(sessionId)}/pieces?seq=${seq}`));
    xhr.withCredentials = true;
    xhr.setRequestHeader("X-CSRF-Token", csrfToken);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (event) => onProgress(event.loaded);
    xhr.onload = () => {
      if (xhr.status === 401) return reject(new NotSignedIn());
      if (xhr.status < 200 || xhr.status >= 300) {
        let body: unknown = {};
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          /* not JSON: a proxy's error page */
        }
        return reject(new ApiError(xhr.status, readDetail(body) ?? `HTTP ${xhr.status}`));
      }
      resolve();
    };
    xhr.onerror = () => reject(new Error("The connection dropped"));
    xhr.onabort = () => reject(new DOMException("Stopped", "AbortError"));
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(piece);
  });
}

export function fileUrl(sessionId: string, kind: "video" | "audio" | "transcript" | "minutes"): string {
  return url(`/api/recordings/${encodeURIComponent(sessionId)}/${kind}`);
}
